import { CSSProperties, memo } from "react"
import { StyledBodyCell } from "baseui/table-grid" // TODO
import { colors, css, fonts } from "../../../Styles"
import { Cell, flexRender, Row } from "@tanstack/react-table"
import { getFrozenColumnStyle, getRemoveRowActionStyle, pinnedWidthOffset } from "./getTableStyles"
import { TableStyles_Types } from "../../../Definitions"
import { isObject } from "lodash"
import { FeedCurve, MilestoneInfo } from "../../../Services"
import { Button_Props } from "../../BaseComponents/Buttons/newButton"

interface TableSharedProps {
  bodyCellStyle: CSSProperties
  columnWidths: number[]
  tableType: TableStyles_Types
  shouldAlwaysRerender?: boolean
  tableToggled?: boolean | number
  highlightedRowId?: number;
}

export const TableContent = ({rows, emptyText, bodyCellStyle, columnWidths, highlightedRowId, tableType, tableToggled, shouldAlwaysRerender, minRows}:{rows: Row<any>[], emptyText: string, minRows?: number} & TableSharedProps) => {
  const amountOfColumns = columnWidths.length;

  const missingRowLength = (minRows ?? 0) - rows.length;
  const missingRows = missingRowLength > 0 ? Array.from({length: missingRowLength}, (_, index) =>
    <EmptyTableRow key={`emptyRow-${index}`} row={rows[0]} index={rows.length + index} bodyCellStyle={bodyCellStyle} columnWidths={columnWidths} tableType={tableType} tableToggled={tableToggled} totalNumberOfRows={rows.length}/>
    ) : [];
  if (rows.length > 0) {
    return (
      [
        ...rows.map((row, index) => (
          <TableRow key={row.id} row={row} highlightedRowId={highlightedRowId} index={index} bodyCellStyle={bodyCellStyle} columnWidths={columnWidths} tableType={tableType} tableToggled={tableToggled} totalNumberOfRows={rows.length} shouldAlwaysRerender={shouldAlwaysRerender}/>
        )),
         ...missingRows
      ]
    )
  }
  return (
    <StyledBodyCell $style={{padding: 0}} $gridColumn={`1/span ${amountOfColumns}`}> <p style={{...css.emptyPen, textAlign: "center", padding: "20px", textTransform: "capitalize", color: (TableStyles_Types.primaryTable || TableStyles_Types.lightTable) ? colors.greyDarkPrimary : colors.greyLightTertiary}}>{emptyText}</p></StyledBodyCell>
  )
}

const TableRow = ({ row, index, bodyCellStyle, columnWidths, tableType, tableToggled, totalNumberOfRows, highlightedRowId, shouldAlwaysRerender }: { row: Row<any>, index: number, totalNumberOfRows: number } & TableSharedProps) => {
  return (
    <div key={index} role="row" style={{
      display: "contents",
      backgroundColor: colors.transparent,
      // outline: isHighlighted ? "solid" : undefined,
      // outlineOffset: "-3px",
      // outlineWidth: "2px",
      // outlineColor: "white",
      // transition: "0.3s ease"
    }}>
      {
        row.getVisibleCells().map((cell, cellIndex) => (
          <MemoizedTableCell key={cell.id} cell={cell} cellIndex={cellIndex} highlightedRowId={highlightedRowId} index={index} bodyCellStyle={bodyCellStyle} columnWidths={columnWidths} tableType={tableType} length={row.getVisibleCells().length} tableToggled={tableToggled} totalNumberOfRows={totalNumberOfRows} shouldAlwaysRerender={shouldAlwaysRerender}/>
        ))
      }
    </div> 
  )
}

const EmptyTableRow = ({ row, index, bodyCellStyle, columnWidths, tableType, tableToggled, totalNumberOfRows}: { row: Row<any>, index: number, totalNumberOfRows: number } & TableSharedProps) => {
  return (
    <div key={index} role="row" style={{
      display: "contents",
      backgroundColor: colors.transparent,
    }}>
      {
        row.getVisibleCells().map((cell, cellIndex) => (
          <EmptyCell key={cell.id} cell={cell} cellIndex={cellIndex} index={index} bodyCellStyle={bodyCellStyle} columnWidths={columnWidths} tableType={tableType} length={row.getVisibleCells().length} tableToggled={tableToggled} totalNumberOfRows={totalNumberOfRows}/>
        ))
      }
    </div> 
  )
}


const EmptyCell = ({cell, cellIndex, index, length, bodyCellStyle, columnWidths, tableType, shouldAlwaysRerender}: {cell: Cell<any, unknown>, cellIndex: number, index: number, length: number, totalNumberOfRows: number} & TableSharedProps) => {
  const isPinned = cell.column.getIsPinned();
  const isLastLeftColumn = cell.column.getIsLastColumn("left");
  const isFirstRightColumn = cell.column.getIsFirstColumn("right");
  const widthOverRide: CSSProperties | undefined = cell.column.columnDef.meta?.width ? { minWidth: "auto" } : undefined

  const style = {
    ...bodyCellStyle,
    ...getRemoveRowActionStyle(cell.column.id), // TODO: consider removing/reworking this
    ...getFrozenColumnStyle(isPinned, isLastLeftColumn, isFirstRightColumn, cellIndex, length, false, tableType, pinnedWidthOffset(isPinned, columnWidths, cellIndex)),
    ...widthOverRide,
    backgroundColor: index % 2 ? tableType === TableStyles_Types.lightTable ? colors.greyLightTertiary : colors.greyLightPrimary : colors.greyLightSecondary,
    transition: "0.3s ease"
  } as CSSProperties;
  return ( <>
    <StyledBodyCell key={cell.id} style={style} $striped={index % 2 ? true : false} >
      {}
    </StyledBodyCell>
  </> );
}
type LockedColumns = Pick<FeedCurve, "feedCurveId"> & Pick<MilestoneInfo, "milestone_id">;
export const LOCKED_COLUMN_NAMES: { [P in keyof LockedColumns]:  P} = {
  milestone_id: "milestone_id",
  feedCurveId: "feedCurveId"
} as const

// clean up, wrap in memo and add a custom value comparison.
const TableCell = ({cell, cellIndex, index, length, highlightedRowId, bodyCellStyle, columnWidths, tableType, shouldAlwaysRerender}: {cell: Cell<any, unknown>, cellIndex: number, index: number, length: number, totalNumberOfRows: number} & TableSharedProps) => {
  const isPinned = cell.column.getIsPinned();
  const isLastLeftColumn = cell.column.getIsLastColumn("left");
  const isFirstRightColumn = cell.column.getIsFirstColumn("right");
  const widthOverRide: CSSProperties | undefined = cell.column.columnDef.meta?.width ? { minWidth: "auto" } : undefined
  
  const dynamicPadding = columnWidths[cellIndex] && columnWidths[cellIndex] < 50 ? "0px 5px" : "0px 16px";

  const isHighlighted = Number(cell.row.original.id) === Number(highlightedRowId);

  let highlightBoxShadow: string | undefined;
  let highlightBorderRadius: string | undefined;
  
  if (isHighlighted) {
    if (cellIndex === 0) {
      highlightBoxShadow = `inset 4px 0 0 0 ${colors.whitePrimary}, inset 0 -4px 0 0 ${colors.whitePrimary}, inset 0 4px 0 0 ${colors.whitePrimary}`;
      highlightBorderRadius = "10px 0 0 10px";
    } else if (cellIndex === length - 1) {
      highlightBoxShadow = `inset -4px 0 0 0 ${colors.whitePrimary}, inset 0 -4px 0 0 ${colors.whitePrimary}, inset 0 4px 0 0 ${colors.whitePrimary}`;
      highlightBorderRadius = "0 10px 10px 0";
    } else {
      highlightBoxShadow = `inset 0 -4px 0 0 ${colors.whitePrimary}, inset 0 4px 0 0 ${colors.whitePrimary}`;
      highlightBorderRadius = "0";
    }
  }
  
  const style = {
    ...bodyCellStyle,
    ...getRemoveRowActionStyle(cell.column.id), // TODO: consider removing/reworking this
    ...getFrozenColumnStyle(isPinned, isLastLeftColumn, isFirstRightColumn, cellIndex, length, false, tableType, pinnedWidthOffset(isPinned, columnWidths, cellIndex)),
    ...widthOverRide,
    backgroundColor: index % 2 ? tableType === TableStyles_Types.lightTable ? colors.greyLightTertiary : colors.greyLightPrimary : colors.greyLightSecondary,
    boxShadow: highlightBoxShadow,
    borderRadius: highlightBorderRadius,
    transition: "0.3s ease",
    padding: dynamicPadding,
  } as CSSProperties;
  return ( <>
    <StyledBodyCell key={cell.id} style={style} $striped={index % 2 ? true : false} >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </StyledBodyCell>
  </> );
}

const MemoizedTableCell = memo(TableCell, (previousProps, newProps) => {
  if (newProps.shouldAlwaysRerender || previousProps.shouldAlwaysRerender) {
    return false;
  }

  if (previousProps.highlightedRowId !== newProps.highlightedRowId) {
    return false;
  }

  // #TODO
  if (previousProps.tableToggled !== newProps.tableToggled) {
    return false;
  }
  
  if (previousProps.cell.row.original.id !== newProps.cell.row.original.id) {
    return false;
  }

  // TODO CTO, this might be possible to simplify this if all button in table are changed to onMouseUp allowing the action to happen after rerender.
  if (newProps.cell.column.columnDef.meta?.forceRerenderOnTableEdit) {
    if (previousProps.totalNumberOfRows !== newProps.totalNumberOfRows) {
      return false;
    }

    // specific fix for feed curves and feed spreading as some row buttons might need re-rendering on milestone changes
    if ("day" in newProps.cell.row.original && typeof newProps.cell.row.original.day === "number" && "day" in previousProps.cell.row.original && typeof previousProps.cell.row.original.day === "number") {
      if (newProps.cell.row.original.day !== previousProps.cell.row.original.day) {
        return false;
      }
    }
  }

  if (newProps.cell.column.columnDef.id === LOCKED_COLUMN_NAMES.milestone_id && LOCKED_COLUMN_NAMES.feedCurveId in newProps.cell.row.original && typeof newProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId] === "object" && "id" in newProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId] && typeof newProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId].id === "number" && LOCKED_COLUMN_NAMES.feedCurveId in previousProps.cell.row.original &&  typeof previousProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId] === "object" && "id" in previousProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId] && typeof previousProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId].id ==="number") {
    if (newProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId].id !== previousProps.cell.row.original[LOCKED_COLUMN_NAMES.feedCurveId].id) {
      return false;
    }
  }
  
  // return true;
  const oldValue = previousProps.cell.getValue() as any;
  const newValue = newProps.cell.getValue() as any;


  if (oldValue !== newValue) {
    if (isObject(oldValue) && isObject(newValue)) {
      
      if (isValueWithButtonProps(newValue) && isValueWithButtonProps(oldValue)) {
        if (newValue.props.overrides?.baseStyles?.visibility !== oldValue.props.overrides?.baseStyles?.visibility) {
          return false;
        }
        if (newValue.props.onClick !== oldValue.props.onClick) {
          return false;
        }
        return true;
      }

      if ((oldValue as any).id && (newValue as any).id) {
        if ((oldValue as any).id === (newValue as any).id) {
          return true;
        }
      }

      return false;
    }

    return false;
  }

  return true;
})

const isValueWithButtonProps = (value: object): value is ValueWithButtonProps => {
  if ("props" in value && value.props && typeof value.props === "object" && "buttonType" in value.props && value.props.buttonType && typeof value.props.buttonType === "string" ) {
    return true;
  }
  return false;

}

interface ValueWithButtonProps {
  props: Button_Props
}